// ─── HelpCenter — botão "Ajuda" + manual do operador (variante-aware) ──
// Fase 1 (protótipo). Botão flutuante "?" que abre um manual integrado.
// Autossuficiente. O conteúdo é escolhido pela VARIANTE do perfil (uiVariant):
//   fuel (Move Express) · petrotec · generic — cada uma com o seu manual.
//   O botão só aparece se existir manual para a variante do tenant.
// Estilo: tema escuro (navy) + laranja de marca + fonte Geist (igual à
//   apresentação de infraestrutura cloud).
import React, { useState, useEffect } from 'react';
import { Dialog } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CloseIcon from '@material-ui/icons/Close';

// Paleta — tons claros de branco/bege (leve e elegante), acento verde EcooGPS.
const C = {
  bg: '#FAF7F2', surface: '#FFFFFF', surface2: '#F6F1E9', sidebar: '#F1E9DB', line: '#EAE1D2', border: '#E0D6C4',
  ink: '#3B382F', muted: '#9A9183',
  accent: '#15803d', accentGlow: 'rgba(21,128,61,.22)', accentSoft: '#eaf4ec',
  sky: '#38BDF8', teal: '#0d9488', violet: '#A78BFA', amber: '#d97706', err: '#dc2626', ok: '#16a34a',
  shadow: '0 1px 2px rgba(60,50,30,.05),0 10px 30px rgba(60,50,30,.08)',
  font: "'Geist',system-ui,-apple-system,'Segoe UI',sans-serif",
};

// Carrega a fonte Geist (Google Fonts) uma só vez.
function useGeistFont() {
  useEffect(() => {
    const id = 'geist-font-link';
    if (document.getElementById(id)) return;
    const l = document.createElement('link');
    l.id = id; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(l);
  }, []);
}

// ── Diagrama da cronologia (variante fuel) ─────────────────────────────
const PHASE1 = [
  { ic: '🚶', lb: 'Início de viagem',     auto: false },
  { ic: '📍', lb: 'Chegada ao terminal',  auto: true },
  { ic: '⛽', lb: 'Início de carregamento', auto: false },
  { ic: '🧾', lb: 'Fim de carregamento',  auto: false },
  { ic: '📤', lb: 'Saída do terminal',    auto: true },
];
const PHASE2 = [
  { ic: '📍', lb: 'Chegada ao P.A.',      auto: true, star: true },
  { ic: '🚚', lb: 'Entrega iniciada',     auto: false },
  { ic: '✅', lb: 'Entrega concluída',    auto: false },
  { ic: '📤', lb: 'Saída do P.A.',        auto: true },
  { ic: '🏠', lb: 'Fim de viagem',        auto: false },
];
const autoBg = 'linear-gradient(135deg,#34d399,#0d9488)';
const manBg = 'linear-gradient(135deg,#fbbf24,#d97706)';

function Node({ n }) {
  const col = n.auto ? C.teal : C.amber;
  const tint = n.auto ? 'rgba(13,148,136,.10)' : 'rgba(217,119,6,.10)';
  const tintBr = n.auto ? 'rgba(13,148,136,.28)' : 'rgba(217,119,6,.28)';
  return (
    <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 90 }}>
      <div style={{ width: 46, height: 46, borderRadius: '50%', background: n.auto ? autoBg : manBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, boxShadow: `0 6px 16px ${n.auto ? 'rgba(13,148,136,.28)' : 'rgba(217,119,6,.28)'}`, border: '2px solid #fff' }}>{n.ic}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, marginTop: 8, lineHeight: 1.25, minHeight: 28 }}>{n.lb}</div>
      <div style={{ marginTop: 5, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3, padding: '3px 7px', borderRadius: 999, background: tint, color: col, border: `1px solid ${tintBr}`, whiteSpace: 'nowrap' }}>
        {n.auto ? '🛰️ auto + manual' : '✍️ manual'}{n.star ? '*' : ''}
      </div>
    </div>
  );
}
function Arrow() {
  return <div style={{ flex: '0 0 20px', height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: `9px solid ${C.border}` }} />
  </div>;
}
function Row({ items }) {
  return <div style={{ display: 'flex', alignItems: 'flex-start' }}>
    {items.map((n, i) => (<React.Fragment key={n.lb}><Node n={n} />{i < items.length - 1 && <Arrow />}</React.Fragment>))}
  </div>;
}
function PhaseLabel({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: C.muted, margin: '0 0 12px 2px', display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ width: 18, height: 3, borderRadius: 2, background: C.accent, display: 'inline-block' }} />{children}
  </div>;
}

function TimelineSection() {
  return (
    <div>
      <SectionTitle sub={<>Cada etapa é registada de uma de duas formas: <B>automaticamente por GPS</B> (quando a viatura entra/sai da zona de um ponto) ou <B>manualmente pelo operador</B> (quando é preciso registar documentos — ordem, litros, guia). Verde/teal = automático (com botão manual de reforço); laranja = só manual.</>} />

      <NoteBox color={C.amber}><B>🔒 A cronologia só fica disponível depois de a OT estar despachada</B> (com veículo e rota atribuída). Enquanto a OT estiver "por atribuir" / sem rota, nenhum marco pode ser marcado — nem sequer o gestor.</NoteBox>

      <div style={{ marginTop: 24 }}>
        <PhaseLabel>Fase 1 · Terminal de abastecimento</PhaseLabel>
        <Row items={PHASE1} />
      </div>
      <div style={{ marginTop: 26 }}>
        <PhaseLabel>Fase 2 · Posto de abastecimento (P.A.) e regresso</PhaseLabel>
        <Row items={PHASE2} />
      </div>

      <div style={{ display: 'flex', gap: 28, marginTop: 26, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, fontWeight: 700, color: C.ink }}>
          <span style={{ width: 24, height: 24, borderRadius: '50%', background: autoBg, border: '2px solid rgba(255,255,255,.14)' }} />
          <span>Automático por GPS <small style={{ display: 'block', fontSize: 10.5, fontWeight: 500, color: C.muted }}>marca-se sozinho na zona (200 m, ~3 min) · botão manual de reforço</small></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, fontWeight: 700, color: C.ink }}>
          <span style={{ width: 24, height: 24, borderRadius: '50%', background: manBg, border: '2px solid rgba(255,255,255,.14)' }} />
          <span>Só manual <small style={{ display: 'block', fontSize: 10.5, fontWeight: 500, color: C.muted }}>exige registo do operador (ordem, litros, guia, fotos, destino)</small></span>
        </div>
      </div>

      <div style={{ marginTop: 26, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ border: `1px solid rgba(13,148,136,.25)`, background: 'rgba(13,148,136,.06)', borderRadius: 14, padding: '15px 17px' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.teal, marginBottom: 7 }}>📍 Chegada ao P.A. — regra da zona</div>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>
            A "Chegada ao P.A." só desbloqueia quando a viatura está <b style={{ color: C.ink }}>dentro da zona do P.A.</b> (até
            200 m do posto da ordem). Se ainda não chegou, o botão fica <b style={{ color: C.ink }}>bloqueado</b>. Estando no
            P.A. ~3 min, marca-se <b style={{ color: C.ink }}>sozinha</b> por GPS.
          </div>
        </div>
        <div style={{ border: `1px solid rgba(217,119,6,.28)`, background: 'rgba(217,119,6,.06)', borderRadius: 14, padding: '15px 17px' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.amber, marginBottom: 7 }}>★ Validação manual do GESTOR</div>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>
            O <b style={{ color: C.ink }}>Gestor</b> pode marcar a "Chegada ao P.A." <b style={{ color: C.ink }}>mesmo fora da
            zona</b> (falha de GPS, ou paragem noutro P.A.) — com <b style={{ color: C.ink }}>confirmação</b>. O
            <b style={{ color: C.ink }}> Operador</b> continua bloqueado. O papel define-se em <i>Permissão</i>.
          </div>
        </div>
      </div>

      {/* Resumo por tipo (3 categorias) */}
      <div style={{ marginTop: 28 }}>
        <PhaseLabel>Resumo por tipo</PhaseLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <CatCard icon="🛰️" title="Automáticos por GPS" color={C.teal}>
            (com botão manual de reforço) — os <B>4 marcos de localização</B>: Chegada ao terminal · Saída do terminal · Chegada ao P.A. · Saída do P.A. <B>São exactamente os que dependem de ambos.</B>
          </CatCard>
          <CatCard icon="✍️" title="Só manuais" color={C.amber}>
            Marcos de <B>ação/documento</B> e de <B>viagem</B>: Início de carregamento · Fim de carregamento · Entrega iniciada · Entrega concluída · Início de viagem · Fim de viagem.
          </CatCard>
          <CatCard icon="ℹ️" title="Porquê manuais" color={C.muted}>
            <B>Carregamento/entrega</B> exigem registar ordem, litros, guia e fotos. <B>Início/Fim de viagem</B> ficaram manuais (falta definir coordenadas da base).
          </CatCard>
        </div>
      </div>

      {/* Tabela completa dos marcos */}
      <div style={{ marginTop: 28 }}>
        <PhaseLabel>Todos os marcos, passo a passo</PhaseLabel>
        <MarcosTable />
      </div>

      <div style={{ marginTop: 18, background: C.surface2, border: `1px solid ${C.line}`, borderLeft: `4px solid ${C.accent}`, borderRadius: 12, padding: '13px 17px', fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>
        <b style={{ color: C.ink }}>Importante:</b> o destino é sempre o <b style={{ color: C.ink }}>P.A. da ordem</b>. Uma
        paragem noutro P.A. não é assumida como destino nem marca a chegada automaticamente. Na
        <b style={{ color: C.ink }}> entrega dividida</b> existem ainda <b style={{ color: C.ink }}>Chegada ao P.A. 2</b> e
        <b style={{ color: C.ink }}> Entrega P.A. 2</b> (manuais).
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: C.muted, fontWeight: 500 }}>* O botão manual da Chegada ao P.A. respeita a zona (exceto validação do gestor).</div>
    </div>
  );
}

function ComingSoon({ label }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: C.muted, gap: 10 }}>
      <div style={{ fontSize: 46 }}>🛠️</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{label}</div>
      <div style={{ fontSize: 13 }}>Esta secção do manual está em preparação.</div>
    </div>
  );
}

// ── Helpers de conteúdo (tema claro) ──────────────────────────────────
const B = ({ children }) => <b style={{ color: C.ink }}>{children}</b>;
// O título da secção vai no cabeçalho do conteúdo; aqui fica só a introdução
// (lead), com uma barra de acento à esquerda. `children` (título) é ignorado.
function SectionTitle({ sub }) {
  if (!sub) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
      <span style={{ flex: '0 0 3px', borderRadius: 3, background: C.accent, alignSelf: 'stretch' }} />
      <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.65, margin: 0, maxWidth: 840 }}>{sub}</p>
    </div>
  );
}
function StepList({ steps }) {
  return (
    <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 13 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 30px', height: 30, borderRadius: 9, background: C.accentSoft, color: C.accent, border: '1px solid rgba(21,128,61,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>{i + 1}</div>
          <div style={{ paddingTop: 3 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{s.t}</div>
            {s.d && <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginTop: 3 }}>{s.d}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
function InfoGrid({ cards }) {
  return (
    <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      {cards.map((c, i) => (
        <div key={i} style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 14, padding: '15px 17px', boxShadow: C.shadow }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, marginBottom: 7, display: 'flex', gap: 8, alignItems: 'center' }}><span>{c.icon}</span>{c.title}</div>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>{c.text}</div>
        </div>
      ))}
    </div>
  );
}
function NoteBox({ children, color }) {
  return <div style={{ marginTop: 18, background: C.surface2, border: `1px solid ${C.line}`, borderLeft: `4px solid ${color || C.accent}`, borderRadius: 12, padding: '13px 17px', fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>{children}</div>;
}
// Cartão de categoria (resumo por tipo) — cor de acento à esquerda.
function CatCard({ icon, title, color, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 14, padding: '15px 16px', boxShadow: C.shadow, borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: C.ink, marginBottom: 8, display: 'flex', gap: 7, alignItems: 'center' }}><span>{icon}</span>{title}</div>
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}
// Tabela de referência de todos os marcos (do deck cronologia-marcos).
const MARCOS_ROWS = [
  ['1', 'Início de viagem', 'man', 'Operador marca'],
  ['2', 'Chegada ao terminal', 'auto', 'Auto na zona do terminal (200 m, ~3 min)'],
  ['3', 'Início de carregamento', 'man', 'Operador marca'],
  ['4', 'Fim de carregamento', 'man', 'Ordem Sonangol + litros + foto'],
  ['5', 'Saída do terminal', 'auto', 'Auto ao sair da zona do terminal'],
  ['6', 'Chegada ao P.A.', 'auto', 'Auto na zona do P.A.; botão manual só na zona (gestor pode forçar)'],
  ['7', 'Entrega iniciada', 'man', 'Operador marca'],
  ['8', 'Entrega concluída', 'man', 'Guia de conformidade + foto'],
  ['9', 'Saída do P.A.', 'auto', 'Auto ao sair da zona do P.A.'],
  ['10', 'Fim de viagem', 'man', 'Exige "Saída do P.A." + próximo destino'],
];
function MarcosTable() {
  const th = { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .4, color: C.muted, textAlign: 'left', padding: '9px 12px', borderBottom: `2px solid ${C.border}` };
  const td = { fontSize: 12, fontWeight: 600, color: C.muted, padding: '9px 12px', borderBottom: `1px solid ${C.line}`, verticalAlign: 'top', lineHeight: 1.45 };
  return (
    <div style={{ marginTop: 14, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', background: C.surface }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: C.font }}>
        <thead><tr>
          <th style={{ ...th, width: '5%' }}>#</th>
          <th style={{ ...th, width: '30%' }}>Marco</th>
          <th style={{ ...th, width: '22%' }}>Tipo</th>
          <th style={{ ...th, width: '43%' }}>Como se regista</th>
        </tr></thead>
        <tbody>
          {MARCOS_ROWS.map((r, i) => {
            const isAuto = r[2] === 'auto';
            return (
              <tr key={r[0]} style={{ background: i % 2 ? '#fcfaf6' : '#fff' }}>
                <td style={{ ...td, fontWeight: 800, color: C.ink }}>{r[0]}</td>
                <td style={{ ...td, fontWeight: 800, color: C.ink }}>{r[1]}</td>
                <td style={td}>
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap', background: isAuto ? 'rgba(13,148,136,.1)' : 'rgba(217,119,6,.1)', color: isAuto ? C.teal : C.amber, border: `1px solid ${isAuto ? 'rgba(13,148,136,.28)' : 'rgba(217,119,6,.28)'}` }}>
                    {isAuto ? '🛰️ Auto + manual' : '✍️ Só manual'}
                  </span>
                </td>
                <td style={td}>{r[3]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Secção: Criar OT de combustível ────────────────────────────────────
function CreateSection() {
  return (
    <div>
      <SectionTitle sub="A Ordem de Transporte (OT) de combustível é o pedido de distribuição. Cria-se num assistente dedicado que já traz os campos certos (produto, cisterna, terminal, cliente e destino).">
        Criar OT de combustível
      </SectionTitle>
      <StepList steps={[
        { t: 'Abrir "Nova Ordem de Combustível"', d: 'No ecrã Ordens, botão "Nova Ordem de Serviço" → opção Combustível. Abre o assistente dedicado.' },
        { t: 'Produto e capacidade', d: 'Escolhe o combustível e a capacidade da cisterna (20 a 60 m³). Os litros esperados são calculados automaticamente — podes ajustar à mão se preciso.' },
        { t: 'Onde carrega (terminal)', d: 'Indica o terminal de recolha (ex.: TCPL, IBV).' },
        { t: 'Cliente final', d: 'Sonangol, Total, Pumangol ou Sonangalp.' },
        { t: 'Destino (P.A.)', d: 'Indica o posto de entrega. Se ainda não se sabe, deixa "PA por confirmar" (escolhendo a província) — o P.A. real revela-se depois, no carregamento.' },
        { t: '(Opcional) Pedido composto', d: 'Liga o modo composto para criar 2 cisternas em simultâneo (LINK).' },
        { t: 'Observações e gravar', d: 'Acrescenta notas / data de serviço e grava. A OT fica "por atribuir".' },
      ]} />
      <InfoGrid cards={[
        { icon: '🧾', title: 'Litros esperados ≠ carregados', text: <>Os litros indicados aqui são a <B>expectativa</B>. Os litros <B>reais</B> registam-se mais tarde no marco "Carregou" (com a Ordem Sonangol e foto).</> },
        { icon: '📍', title: '"PA por confirmar"', text: <>Se o posto ainda não é conhecido, escolhe só a <B>província</B>. O P.A. real é revelado no carregamento e a rota ajusta-se.</> },
      ]} />
      <NoteBox>Depois de criada, a OT fica <B>"por atribuir"</B> até a despachares (associar veículo e motorista) — ver a secção seguinte.</NoteBox>
    </div>
  );
}

// ── Secção: Associar veículo / cisterna / motorista ───────────────────
function AssignSection() {
  return (
    <div>
      <SectionTitle sub="Despachar a OT é associá-la a um veículo e motorista (e, se for o caso, à cisterna atrelada). É o despacho que arranca a rota.">
        Associar veículo / cisterna / motorista
      </SectionTitle>
      <StepList steps={[
        { t: 'Escolher a OT "por atribuir"', d: 'Na listagem de Ordens, seleciona a OT e abre o despacho.' },
        { t: 'Veículo e motorista', d: 'Escolhe a viatura (tractor ou rígido) e o motorista.' },
        { t: 'Cisterna atrelada', d: 'Se for tractor + reboque, escolhe a cisterna (reboque) no selecionador de cisternas.' },
        { t: 'Local de partida', d: 'Posição atual do veículo, base, ou terminal — conforme o caso.' },
        { t: 'Atribuir e planear rota', d: 'Carrega no botão "Atribuir e planear rota": a OT passa a "atribuída" e é gerada a rota. Só a partir daqui é que a cronologia fica disponível.' },
      ]} />
      <InfoGrid cards={[
        { icon: '🚛', title: 'Tipologias de cisterna', text: <>O sistema distingue <B>tractor</B>, <B>rígido</B> e <B>reboque</B> automaticamente, a partir da configuração do veículo no EcooGPS.</> },
        { icon: '❓', title: 'A cisterna não aparece?', text: <>A cisterna tem de estar <B>bem configurada no EcooGPS</B> (categoria correta + capacidade "X M3" no modelo). Se não aparecer no selecionador, é aí que se corrige.</> },
      ]} />
      <NoteBox>O despacho é <B>muitos-para-muitos</B>: podes gerir vários veículos e OTs de forma flexível.</NoteBox>
    </div>
  );
}

// ── Secção: Criar e calcular a rota ───────────────────────────────────
function RouteSection() {
  return (
    <div>
      <SectionTitle sub="A rota nasce da OT e liga 3 pontos: a partida, o terminal de carregamento e o P.A. de entrega. É calculada para camião, com tempos realistas.">
        Criar e calcular a rota
      </SectionTitle>
      <StepList steps={[
        { t: 'A rota nasce da OT', d: '3 pontos: Partida → Terminal (recolha) → P.A. (entrega).' },
        { t: 'Cálculo para camião', d: 'A rota é calculada com tempos realistas de camião (provider HERE no Move Express).' },
        { t: 'Gravar rota da OT', d: 'Guarda a rota calculada na OT.' },
        { t: 'Piloto automático', d: 'Ao marcar "Fim de carregamento", a rota recalcula-se e grava-se sozinha, voltando à cronologia — sem ires ao planeador.' },
        { t: 'Mudar o destino', d: 'Se editares o P.A., o Planner pergunta "Atualizar a rota no mapa?" para a geometria acompanhar o novo destino.' },
        { t: 'Entrega dividida', d: 'Podes dividir a entrega por 2 P.A. — a rota para os dois postos é tratada automaticamente.' },
      ]} />
      <InfoGrid cards={[
        { icon: '🏁', title: 'Local de partida', text: <>Pode ser a <B>posição atual do veículo</B>, a base, ou o terminal — conforme o tipo de ordem e a configuração.</> },
        { icon: '🔄', title: 'Recálculo / re-plano', text: <>Ao recalcular uma rota em curso, o Planner <B>exclui as OTs já entregues</B> e parte da <B>posição GPS atual</B> da viatura.</> },
      ]} />
      <NoteBox>No Move Express a rota de pesados usa o <B>HERE (modo camião)</B> — ETAs mais realistas para cisterna.</NoteBox>
    </div>
  );
}

// ── Secção: Dashboard, divergências e relatórios ──────────────────────
function ReportsSection() {
  return (
    <div>
      <SectionTitle sub="O dashboard de combustível mostra a operação em números. Tem duas leituras: a atividade do período (tudo) e a comparação carga↔entrega (só OTs já entregues).">
        Dashboard, divergências e relatórios
      </SectionTitle>
      <InfoGrid cards={[
        { icon: '⛽', title: 'Litros Carregados (TCPL/IBV)', text: <>Todo o combustível <B>carregado</B> nos terminais no período (inclui OTs ainda por entregar).</> },
        { icon: '🚚', title: 'Litros Entregues (PAs)', text: <>Todo o combustível <B>entregue</B> nos postos no período.</> },
        { icon: '🧮', title: 'Carregado (OTs entregues)', text: <>Só o carregado das OTs <B>já entregues</B>. A diferença para o "Litros Carregados" é o que está <B>ainda por entregar</B>.</> },
        { icon: '⚖️', title: 'Divergência total', text: <>Carregado(OTs entregues) <B>−</B> Entregue, só sobre OTs já entregues (par carga+entrega). "Com divergência" = nº de OTs com desvio.</> },
      ]} />
      <NoteBox>A divergência <B>só compara OTs já entregues</B> — por isso não é a subtração dos dois totais de topo (esses incluem cargas ainda por entregar).</NoteBox>
      <NoteBox color={C.teal}>Há ainda gráficos de <B>Carregamentos por terminal</B> e <B>Entregas por cliente final</B>, e <B>relatórios exportáveis</B> (PDF/Excel) por período.</NoteBox>
    </div>
  );
}

// ── Manuais por VARIANTE do perfil ─────────────────────────────────────
// Para adicionar a Petrotec no futuro: criar aqui MANUALS.petrotec com as
// suas secções (assistência + app motorista). O botão aparece automaticamente
// para quem tiver essa variante.
const MANUALS = {
  fuel: {
    subtitle: 'Guia do operador · Combustível',
    sections: [
      { key: 'create',   icon: '⛽', label: 'Criar OT de combustível',                ready: true },
      { key: 'assign',   icon: '🚚', label: 'Associar veículo / cisterna / motorista', ready: true },
      { key: 'route',    icon: '🗺️', label: 'Criar e calcular a rota',                 ready: true },
      { key: 'timeline', icon: '📍', label: 'Cronologia da distribuição',              ready: true },
      { key: 'reports',  icon: '📊', label: 'Dashboard, divergências e relatórios',    ready: true },
    ],
    content: (active, label) => {
      if (active === 'create') return <CreateSection />;
      if (active === 'assign') return <AssignSection />;
      if (active === 'route') return <RouteSection />;
      if (active === 'timeline') return <TimelineSection />;
      if (active === 'reports') return <ReportsSection />;
      return <ComingSoon label={label} />;
    },
  },
  // petrotec: { subtitle: 'Guia do técnico · Assistência', sections: [...], content: ... },
};

export default function HelpCenter({ variant = 'generic', lang = 'pt' }) {
  const manual = MANUALS[variant];
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('create');
  useGeistFont();
  // Abre a partir do ícone de Ajuda na topbar (Header dispara este evento).
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener('openHelpCenter', h);
    return () => window.removeEventListener('openHelpCenter', h);
  }, []);

  if (!manual) return null; // sem manual para esta variante

  const idx = Math.max(0, manual.sections.findIndex(s => s.key === active));
  const cur = manual.sections[idx] || manual.sections[0];

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullScreen
      PaperProps={{ style: { background: C.bg } }}>
      <div style={{ display: 'flex', height: '100%', fontFamily: C.font, background: C.bg }}>
        {/* ── Sidebar ── */}
        <div style={{ width: 322, flexShrink: 0, background: C.sidebar, borderRight: `1px solid ${C.border}`, padding: '26px 18px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px' }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 16px ${C.accentGlow}` }}>
              <HelpOutlineIcon style={{ fontSize: 20, color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: C.ink, lineHeight: 1.1 }}>Guia do operador</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginTop: 2 }}>{manual.subtitle}</div>
            </div>
          </div>
          <div style={{ height: 1, background: C.border, margin: '18px 4px 14px' }} />
          {/* Stepper vertical — números ligados por um traço, para mostrar a
              sequência das etapas (em árvore) em vez de números soltos. */}
          {manual.sections.map((s, i) => {
            const isActive = s.key === active;
            const last = i === manual.sections.length - 1;
            return (
              <button key={s.key} type="button" disabled={!s.ready} onClick={() => s.ready && setActive(s.key)}
                style={{
                  textAlign: 'left', border: 'none', outline: 'none', background: 'transparent',
                  cursor: s.ready ? 'pointer' : 'not-allowed', padding: 0,
                  display: 'flex', alignItems: 'stretch', fontFamily: C.font,
                }}>
                {/* Carril: linha acima + número + linha abaixo (contínuo entre etapas) */}
                <div style={{ width: 34, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 2, flex: 1, background: i === 0 ? 'transparent' : C.border }} />
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800,
                    background: isActive ? C.accent : '#fff',
                    color: isActive ? '#fff' : (s.ready ? C.accent : 'rgba(59,56,47,.4)'),
                    border: `2px solid ${isActive ? C.accent : (s.ready ? 'rgba(21,128,61,.4)' : C.border)}`,
                    boxShadow: isActive ? `0 2px 8px ${C.accentGlow}` : 'none',
                  }}>{s.ready ? i + 1 : '·'}</span>
                  <span style={{ width: 2, flex: 1, background: last ? 'transparent' : C.border }} />
                </div>
                {/* Conteúdo */}
                <span style={{
                  flex: 1, margin: '3px 0 3px 2px', padding: '9px 11px', borderRadius: 11,
                  background: isActive ? '#fff' : 'transparent', boxShadow: isActive ? C.shadow : 'none',
                  display: 'flex', alignItems: 'center', gap: 9,
                  color: s.ready ? C.ink : 'rgba(59,56,47,.4)', fontSize: 13, fontWeight: isActive ? 800 : 600, lineHeight: 1.3,
                }}>
                  <span style={{ fontSize: 15 }}>{s.icon}</span>
                  <span style={{ flex: 1 }}>{s.label}</span>
                  {!s.ready && <span style={{ fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase', background: 'rgba(0,0,0,.05)', color: C.muted, padding: '2px 6px', borderRadius: 999 }}>em breve</span>}
                </span>
              </button>
            );
          })}
          <div style={{ marginTop: 'auto', fontSize: 10.5, color: C.muted, padding: '14px 6px 0', borderTop: `1px solid ${C.border}`, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.accent }} /> EcooGPS · Planner
          </div>
        </div>

        {/* ── Conteúdo ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: C.bg }}>
          <div style={{ flex: '0 0 auto', height: 3, background: `linear-gradient(90deg,${C.accent},${C.teal})` }} />
          {/* Cabeçalho do conteúdo — migalha + contador + fechar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 40px', borderBottom: `1px solid ${C.line}`, background: C.surface }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: C.accentSoft, border: '1px solid rgba(21,128,61,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{cur?.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .7, color: C.muted }}>Distribuição de combustível</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.ink, marginTop: 1, letterSpacing: -.3 }}>{cur?.label}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 999, padding: '6px 12px', whiteSpace: 'nowrap' }}>Secção {idx + 1} de {manual.sections.length}</div>
            <button type="button" onClick={() => setOpen(false)} style={{ border: `1px solid ${C.border}`, background: C.bg, borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, flexShrink: 0 }}>
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>
          {/* Corpo */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '30px 40px 56px' }}>
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
              {manual.content(active, cur?.label)}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
